import { FunctionComponent } from 'react'
import { WChip } from '@alice-health/wonderland-react'
import { useFilters, useList } from '@/modules/accredited-establishments/hooks'
import { getCategoryCount } from '@/modules/accredited-establishments/functions'
import { CATEGORY_CHIP_OPTIONS } from './CategoryChips.constants'

type ChipHost = HTMLElement & { componentOnReady?: () => Promise<unknown> }

/*
 * Override temporario: o `w-chip` crava `mode="primary"` no `w-label` do
 * contador e nao expoe prop para isso, mas o layout pede `tertiary`. Em vez de
 * pintar por CSS, escrevemos o proprio prop nativo do `w-label` com um valor
 * valido da union, depois do componente montar.
 *
 * No chip selecionado o contador acompanha o texto: o `w-chip` nao poe `mode`
 * no label principal, entao os dois ficam no `primary` default.
 *
 * O Stencil compara contra o vnode anterior, e o `mode` do contador nunca muda
 * de valor no vdom, entao ele nao reverte o atributo em re-render.
 *
 * Remover quando o `w-chip` aceitar o mode do contador.
 */
const applyCounterMode = async (chip: ChipHost | null, isActive: boolean) => {
  if (!chip) return

  await customElements.whenDefined('w-chip')
  await chip.componentOnReady?.()

  chip
    .querySelector('.w-chip--counter')
    ?.setAttribute('mode', isActive ? 'primary' : 'tertiary')
}

const CategoryChips: FunctionComponent = () => {
  const { updateCategories, categories } = useFilters()

  /*
   * A contagem vem na mesma resposta da listagem, e o React Query casa pela
   * query key, entao isto reaproveita o cache sem uma segunda chamada. Vale
   * nas duas visoes: a query da listagem tambem roda no mapa.
   */
  const { data } = useList()

  const categoryCounts = data?.pages[0]?.categoryCounts

  const activeCategory = categories[0]

  /* An active category hides the others until the filter is cleared. */
  const visibleOptions = activeCategory
    ? CATEGORY_CHIP_OPTIONS.filter(option => option.value === activeCategory)
    : CATEGORY_CHIP_OPTIONS

  return (
    // `contents` keeps the chips as direct children of the scrolling row.
    <div className='contents'>
      {visibleOptions.map(({ label, value }) => {
        const isActive = value === activeCategory
        const count = getCategoryCount(categoryCounts, value)

        return (
          <WChip
            // `WChip` keeps its first render when a prop changes: remount it.
            key={`${value}-${isActive}-${count}`}
            ref={chip => {
              void applyCounterMode(chip, isActive)
            }}
            className='shrink-0'
            label={label}
            active={isActive}
            trailingIcon={isActive ? 'icClose' : undefined}
            counter={count !== undefined ? String(count) : undefined}
            // `wClick` alone covers select and clear: `updateCategories` toggles.
            onWClick={() => updateCategories(value)}
          />
        )
      })}
    </div>
  )
}

export { CategoryChips }
